/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  keepPreviousData,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type CreateItpInput,
  type GoalStatus,
  type GoalStatusChange,
  itpApi,
  type Page,
  type PlanDetail,
  type PlanListItem,
  type PlansPageParams,
  type TaxonomyGoal,
} from '../api/itp.js';

const PLANS_KEY = ['itps'] as const;
const planKey = (id: string): readonly unknown[] => ['itps', id];
const goalHistoryKey = (planId: string, goalId: string): readonly unknown[] => [
  'itps',
  planId,
  'goals',
  goalId,
  'history',
];

export function usePlans(): UseQueryResult<readonly PlanListItem[]> {
  return useQuery({ queryKey: PLANS_KEY, queryFn: () => itpApi.list() });
}

/** Paginated, searchable plan list for the role-scoped management view (P1-1). */
export function usePlansPage(params: PlansPageParams): UseQueryResult<Page<PlanListItem>> {
  return useQuery({
    queryKey: ['itps', 'page', params],
    queryFn: () => itpApi.listPage(params),
    placeholderData: keepPreviousData,
  });
}

/** The shared, read-only goal-taxonomy library for the ITP builder (BRD M01 #2). */
export function useTaxonomy(): UseQueryResult<readonly TaxonomyGoal[]> {
  return useQuery({
    queryKey: ['itps', 'taxonomy'],
    queryFn: () => itpApi.taxonomy(),
    staleTime: 30 * 60 * 1000,
  });
}

export function useCreatePlan(): UseMutationResult<PlanDetail, Error, CreateItpInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateItpInput) => itpApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_KEY });
    },
  });
}

export function usePlan(id: string, enabled = true): UseQueryResult<PlanDetail> {
  return useQuery({
    queryKey: planKey(id),
    queryFn: () => itpApi.getOne(id),
    enabled: enabled && id !== '',
  });
}

interface GoalProgressVars {
  readonly goalId: string;
  readonly status?: GoalStatus | undefined;
  readonly progressPct?: number | undefined;
  readonly note?: string | undefined;
}

export function useUpdateGoalProgress(
  planId: string,
): UseMutationResult<PlanDetail, Error, GoalProgressVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: GoalProgressVars) =>
      itpApi.updateGoalProgress(planId, vars.goalId, {
        status: vars.status,
        progressPct: vars.progressPct,
        note: vars.note,
      }),
    onSuccess: (plan, vars) => {
      qc.setQueryData(planKey(planId), plan);
      void qc.invalidateQueries({ queryKey: PLANS_KEY });
      void qc.invalidateQueries({ queryKey: goalHistoryKey(planId, vars.goalId) });
    },
  });
}

/** A goal's append-only status-change log (BRD M01 #6); fetched on demand. */
export function useGoalHistory(
  planId: string,
  goalId: string,
  enabled: boolean,
): UseQueryResult<readonly GoalStatusChange[]> {
  return useQuery({
    queryKey: goalHistoryKey(planId, goalId),
    queryFn: () => itpApi.goalHistory(planId, goalId),
    enabled: enabled && planId !== '' && goalId !== '',
  });
}

interface ToggleStepVars {
  readonly goalId: string;
  readonly stepId: string;
  readonly done: boolean;
}

export function useToggleStep(
  planId: string,
): UseMutationResult<PlanDetail, Error, ToggleStepVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: ToggleStepVars) =>
      itpApi.toggleStep(planId, vars.goalId, vars.stepId, vars.done),
    onSuccess: (plan) => {
      qc.setQueryData(planKey(planId), plan);
    },
  });
}

interface ToggleStepItemVars {
  readonly goalId: string;
  readonly stepId: string;
  readonly itemId: string;
  readonly done: boolean;
}

export function useToggleStepItem(
  planId: string,
): UseMutationResult<PlanDetail, Error, ToggleStepItemVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: ToggleStepItemVars) => itpApi.toggleStepItem(planId, vars),
    onSuccess: (plan) => {
      qc.setQueryData(planKey(planId), plan);
    },
  });
}
